// Library imports
import React, { useState } from "react";
import { useDispatch } from "react-redux";

// User Component imports
import { PageContent, PageHeader } from 'components/common/layout_element/LayoutElement';
import { Row, Stack, Text, TextInput, Button } from "_core/components/common/UI";
import { InfoCard } from "components/common/parts";
import * as Constants from "_core/configs/Constants";
import { setLoading, setErrorModal } from "_core/slices/scene/SceneSlice";
import AdminOpsDemoCreateApi from "apis/AdminOpsDemoCreateApi";
import AdminOpsDemoListApi from "apis/AdminOpsDemoListApi";
import AdminOpsDemoUpdateApi from "apis/AdminOpsDemoUpdateApi";
import AdminOpsDemoDeleteApi from "apis/AdminOpsDemoDeleteApi";
import { useOperatorPage } from "./common/useOperatorPage";
import DemoReloadField from "./demo/DemoReloadField";
import DemoPageList from "./demo/DemoPageList";
import DemoEditModal from "./demo/DemoEditModal";
import { useCopyUrl } from "./demo/useCopyUrl";
import {
    DEMO_RELOAD_SEC_DEFAULT, DEMO_TITLE_MAX_LENGTH,
    pageNameError, reloadSecToSend, canCreateDemo,
} from "./utils/demoPage";

import './OpsCommon.scss';
import './AdminOpsDemoPage.scss';

// 作成結果カードのコピー状態のキー（一覧の行はページ名。ページ名に使えない文字で区別する）
const RESULT_COPY_KEY = ":result";

const AdminOpsDemoPage = () => {
    const dispatch = useDispatch();
    const ExecCreate = AdminOpsDemoCreateApi();
    const ExecList = AdminOpsDemoListApi();
    const ExecUpdate = AdminOpsDemoUpdateApi();
    const ExecDelete = AdminOpsDemoDeleteApi();

    const [pageName, setPageName] = useState("");
    const [title, setTitle] = useState("");
    const [isAutoReload, setIsAutoReload] = useState(true);
    const [reloadSec, setReloadSec] = useState(String(DEMO_RELOAD_SEC_DEFAULT));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [payload, setPayload] = useState(null);
    const [editPage, setEditPage] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const showError = (response) => {
        dispatch(setErrorModal({ show: true, text: response?.error_detail ?? { message: Constants.UNKNOWN_ERROR_DETAIL } }));
    };
    const { copiedKey, copyUrl } = useCopyUrl(() => {
        showError({ error_detail: { title: "エラー", message: "クリップボードへのコピーに失敗しました。" } });
    });

    const LoadList = async () => {
        const { response } = await ExecList();
        if (!response || response.error_code !== 0) {
            showError(response);
            return;
        }
        setPayload(response);
    };

    const { canUse } = useOperatorPage(LoadList);

    const nameError = pageNameError(pageName);
    const canSubmit = !isSubmitting && canCreateDemo(pageName, isAutoReload, reloadSec);

    const onChangePageName = (e) => {
        setPageName(e.target.value);
        setResult(null);
    };

    const createDemo = async () => {
        if (!canSubmit) return;
        setIsSubmitting(true);
        const { response } = await ExecCreate(pageName, title.trim(), reloadSecToSend(isAutoReload, reloadSec));
        setIsSubmitting(false);
        if (!response || response.error_code !== 0) {
            showError(response);
            return;
        }
        setResult({ url: response.url });
        LoadList();
    };

    const openUrl = (url) => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const saveDemo = async (newTitle, newReloadSec) => {
        setIsSaving(true);
        dispatch(setLoading(true));
        const { response } = await ExecUpdate(editPage.page_name, newTitle, newReloadSec);
        dispatch(setLoading(false));
        setIsSaving(false);
        if (!response || response.error_code !== 0) {
            showError(response);
            return;
        }
        setPayload(response);
        setEditPage(null);
    };

    const deleteDemo = async () => {
        setIsSaving(true);
        dispatch(setLoading(true));
        const { response } = await ExecDelete(editPage.page_name);
        dispatch(setLoading(false));
        setIsSaving(false);
        if (!response || response.error_code !== 0) {
            showError(response);
            return;
        }
        setPayload(response);
        if (result?.url === editPage.url) setResult(null);
        setEditPage(null);
    };

    return (
        <PageContent pageId="AdminOpsDemoPage">
            <div className="ops-scope width-full">
                <PageHeader>
                    <Row className="flex-both align-center width-full">
                        <Stack className="spacing-4px">
                            <div className="page-header-title">デモページ作成</div>
                            <div className="page-header-detail">現在ログイン中のアカウントの設定で、商談・検証用のデモページを作成します。</div>
                        </Stack>
                    </Row>
                </PageHeader>

                <div className="ops-demo-body">
                    <div className="ops-demo-card">
                        <div className="ops-demo-row">
                            <div className="ops-demo-label">
                                <Row className="flex-left align-center spacing-4px">
                                    <Text className="text-size-medium text-bold ops-demo-label-text">ページ名</Text>
                                    <Text className="ops-demo-required">※必須</Text>
                                </Row>
                                <div className="ops-demo-label-hint">ファイル名・URL に使われます</div>
                            </div>
                            <div className="ops-demo-form">
                                <TextInput className="ops-demo-mono" default={pageName} onchange={onChangePageName}
                                    placeholder="my_demo_page" autocorrect={false} disabled={isSubmitting} />
                                <div className="ops-demo-form-hint">半角英数字・アンダースコア・ハイフンのみ。既に使われているページ名では作成できません</div>
                                {nameError && <div className="ops-demo-form-error">{nameError}</div>}
                            </div>
                        </div>
                        <hr />

                        <div className="ops-demo-row">
                            <div className="ops-demo-label">
                                <Text className="text-size-medium text-bold ops-demo-label-text">ページタイトル</Text>
                                <div className="ops-demo-label-hint">未入力時は既定値が使われます</div>
                            </div>
                            <div className="ops-demo-form">
                                <TextInput default={title} onchange={(e) => setTitle(e.target.value)}
                                    placeholder="サンプル AIコンシェルジュ デモ" disabled={isSubmitting}
                                    rules={[{ type: "maxLength", value: DEMO_TITLE_MAX_LENGTH }]} />
                            </div>
                        </div>
                        <hr />

                        <div className="ops-demo-row">
                            <div className="ops-demo-label">
                                <Row className="flex-left align-center spacing-4px">
                                    <Text className="text-size-medium text-bold ops-demo-label-text">自動リロード</Text>
                                    <Text className="ops-demo-required">※必須</Text>
                                </Row>
                                <div className="ops-demo-label-hint">無操作がこの時間続くとリロードします</div>
                            </div>
                            <DemoReloadField isAutoReload={isAutoReload} reloadSec={reloadSec} disabled={isSubmitting}
                                onChangeAutoReload={setIsAutoReload} onChangeReloadSec={setReloadSec} />
                        </div>

                        <div className="ops-demo-actions">
                            <Button className={"width-320px " + (canSubmit ? "primary" : "primary disabled")} disabled={!canSubmit} onClick={createDemo}>
                                デモページ作成
                            </Button>
                            <InfoCard className="ops-demo-info">
                                <div>作成したデモページは、下の一覧から設定変更・削除できます。</div>
                            </InfoCard>
                        </div>
                    </div>

                    {result &&
                        <div className="ops-demo-result">
                            <div className="ops-demo-result-head">
                                <div className="ops-demo-result-title"><i className="bi bi-check-circle-fill" />作成完了</div>
                                <span className="ops-demo-result-badge">成功</span>
                            </div>
                            <div className="ops-demo-result-url">{result.url}</div>
                            <div className="ops-demo-result-actions">
                                <button type="button" className="ops-demo-result-btn is-primary" onClick={() => openUrl(result.url)}>
                                    <i className="bi bi-box-arrow-up-right" />デモページを開く
                                </button>
                                <button type="button" className="ops-demo-result-btn" onClick={() => copyUrl(RESULT_COPY_KEY, result.url)}>
                                    <i className={"bi " + (copiedKey === RESULT_COPY_KEY ? "bi-check-lg" : "bi-copy")} />{copiedKey === RESULT_COPY_KEY ? "コピーしました" : "URLをコピー"}
                                </button>
                            </div>
                        </div>
                    }

                    <DemoPageList pages={payload?.page_list ?? null} showLoading={canUse} copiedKey={copiedKey}
                        onOpen={(page) => openUrl(page.url)} onCopy={(page) => copyUrl(page.page_name, page.url)}
                        onEdit={setEditPage} />
                </div>

                {editPage &&
                    <DemoEditModal key={editPage.page_name} page={editPage} isSaving={isSaving}
                        onClose={() => setEditPage(null)} onSave={saveDemo} onDelete={deleteDemo} />
                }
            </div>
        </PageContent>
    );
};

export default AdminOpsDemoPage;
